import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Paperclip, 
  Upload, 
  Download, 
  Trash2, 
  File, 
  FileText, 
  FileImage, 
  FileSpreadsheet,
  FileVideo,
  Loader2,
  Eye,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const FileAttachments = ({ entityType, entityId, canEdit = false }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAttachments();
  }, [entityType, entityId]);

  const fetchAttachments = async () => {
    try {
      const response = await api.get(`/attachments/list/${entityType}/${entityId}`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to fetch attachments');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `/attachments/${entityType}/${entityId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toast.success('File uploaded successfully');
      fetchAttachments();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await api.get(`/attachments/${attachment.id}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handlePreview = (attachment) => {
    const ext = attachment.filename.split('.').pop()?.toLowerCase();
    const previewableTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'txt', 'md'];
    
    if (previewableTypes.includes(ext)) {
      setPreviewFile(attachment);
    } else {
      // For non-previewable files, download instead
      handleDownload(attachment);
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
      await api.delete(`/attachments/${attachmentId}`);
      toast.success('File deleted');
      fetchAttachments();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return <FileImage className="w-5 h-5 text-purple-500" />;
    }
    if (['pdf'].includes(ext)) {
      return <FileText className="w-5 h-5 text-red-500" />;
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return <FileSpreadsheet className="w-5 h-5 text-emerald-500" />;
    }
    if (['doc', 'docx', 'txt', 'md'].includes(ext)) {
      return <FileText className="w-5 h-5 text-blue-500" />;
    }
    if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) {
      return <FileVideo className="w-5 h-5 text-pink-500" />;
    }
    return <File className="w-5 h-5 text-slate-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="file-attachments">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Paperclip className="w-4 h-4 text-[#235894]" />
              Attachments ({attachments.length})
            </CardTitle>
            {canEdit && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="file-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-1" />
                  )}
                  Upload
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {attachments.length === 0 ? (
            <div className="text-center py-6 text-slate-400">
              <Paperclip className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">No attachments yet</p>
              {canEdit && (
                <p className="text-xs mt-1">Click upload to add files</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
                  data-testid={`attachment-${attachment.id}`}
                >
                  {getFileIcon(attachment.filename)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {attachment.filename}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDate(attachment.uploaded_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handlePreview(attachment)}
                      title="Preview"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDownload(attachment)}
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(attachment.id)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewFile && getFileIcon(previewFile.filename)}
              {previewFile?.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {previewFile && (() => {
              const ext = previewFile.filename.split('.').pop()?.toLowerCase();
              const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
              const previewUrl = `${apiUrl}/api/attachments/${previewFile.id}`;
              
              if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
                return (
                  <img
                    src={previewUrl}
                    alt={previewFile.filename}
                    className="max-w-full h-auto rounded-lg"
                  />
                );
              }
              
              if (ext === 'pdf') {
                return (
                  <iframe
                    src={previewUrl}
                    className="w-full h-[70vh] rounded-lg border"
                    title={previewFile.filename}
                  />
                );
              }
              
              return (
                <div className="text-center py-8">
                  <File className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Preview not available for this file type</p>
                  <Button
                    className="mt-4"
                    onClick={() => handleDownload(previewFile)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download File
                  </Button>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FileAttachments;
